import React, { useState } from 'react';
import { FaPlus } from 'react-icons/fa';
import { useCanvas } from '../../../../context/CanvasContext';
import { TUTORIAL_TARGETS, tutorialAttr } from '../../../../tutorials';
import KeyInlineCreateForm from './KeyInlineCreateForm';
import styles from './styles/Keys.module.css';

/**
 * Pick an existing key, or make a new one on the spot.
 *
 * A freshly created key goes through the same `onPick` as an existing one, so a
 * caller never has to care which it got: one gesture creates the key and
 * attaches it, instead of a detour to the Keys panel and back.
 *
 * @param {{
 *   heading: string,
 *   excludeIds?: string[],
 *   onPick: (keyId: string) => void,
 *   onCancel: () => void,
 *   emptyText?: string,
 * }} props
 */
const KeyPicker = ({
    heading,
    excludeIds = [],
    onPick,
    onCancel,
    emptyText = 'No other keys yet. Create one below.',
}) => {
    const { keys, createKey } = useCanvas();
    const excluded = new Set(excludeIds.map(String));
    const available = keys.filter((key) => !excluded.has(String(key.id)));

    // Nothing to pick from means the only useful thing to show is the form.
    const [isCreating, setIsCreating] = useState(keys.length === 0);

    const handleCreate = (data) => {
        const createdId = createKey(data);
        if (createdId) onPick(createdId);
    };

    return (
        <div className={styles.picker} {...tutorialAttr(TUTORIAL_TARGETS.KEY_PICKER)}>
            <h4 className={styles.pickerHeading}>{isCreating ? 'New key' : heading}</h4>

            {isCreating ? (
                <KeyInlineCreateForm
                    onCreate={handleCreate}
                    onCancel={() => (keys.length === 0 ? onCancel() : setIsCreating(false))}
                />
            ) : (
                <>
                    {available.length > 0 ? (
                        <ul className={styles.pickerList}>
                            {available.map((key) => (
                                <li key={key.id}>
                                    <button
                                        type="button"
                                        className={styles.pickerItem}
                                        onClick={() => onPick(key.id)}
                                    >
                                        <span className={styles.pickerItemName}>{key.name}</span>
                                        {key.description && (
                                            <span className={styles.pickerItemDescription}>
                                                {key.description}
                                            </span>
                                        )}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className={styles.muted}>{emptyText}</p>
                    )}
                    <button
                        type="button"
                        className={styles.newKeyRow}
                        onClick={() => setIsCreating(true)}
                    >
                        <FaPlus size={10} /> New key…
                    </button>
                    <div className={styles.pickerActions}>
                        <button type="button" className={styles.secondaryButton} onClick={onCancel}>
                            Cancel
                        </button>
                    </div>
                </>
            )}
        </div>
    );
};

export default KeyPicker;
